// src/firebaseConfig.js
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  setPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
} from "firebase/app-check";

const firebaseConfig = {
  apiKey: "AIzaSyAbnJIq5rlCYW5ERM5qNO4R_Lgc2XbKF-I",
  authDomain: "invoice-app-v2-9aee2.firebaseapp.com",
  projectId: "invoice-app-v2-9aee2",
  storageBucket: "invoice-app-v2-9aee2.firebasestorage.app",
  messagingSenderId: "418118401976",
  appId: "1:418118401976:web:3f866d89d7d20944f288d1",
};

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, "us-central1");

let _persistencePromise = null;

export function isFirebaseAuthStorageError(error) {
  const code = String(error?.code || "").toLowerCase();
  const name = String(error?.name || "").toLowerCase();
  const message = String(error?.message || error || "").toLowerCase();
  return (
    code.includes("quota") ||
    name.includes("quota") ||
    message.includes("quota") ||
    message.includes("exceeded the quota") ||
    message.includes("failed to execute 'setitem'") ||
    message.includes("firebase:authuser") ||
    message.includes("indexeddb")
  );
}

export function ensureSessionPersistence() {
  if (_persistencePromise) return _persistencePromise;

  // Use session persistence for MatrixOS layer logins. It avoids the browser
  // localStorage/IndexedDB quota trap that can block staff from logging in on
  // shared tills and long-running store devices.
  _persistencePromise = setPersistence(auth, browserSessionPersistence)
    .catch(async (e) => {
      console.warn("[auth] session persistence failed, falling back to memory:", e?.message || e);
      try {
        await setPersistence(auth, inMemoryPersistence);
      } catch (memoryError) {
        console.warn("[auth] memory persistence failed:", memoryError?.message || memoryError);
      }
    });

  return _persistencePromise;
}

export async function repairFirebaseAuthStorage() {
  if (typeof window === "undefined") return false;

  let changed = false;
  try {
    const clearMatchingKeys = (storage) => {
      if (!storage) return;
      const keys = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (!key) continue;
        const lower = key.toLowerCase();
        if (
          lower.includes("firebase:authuser") ||
          lower.includes("firebase:host") ||
          lower.includes("firebase:previous_websocket_failure") ||
          lower.includes("firebase-heartbeat") ||
          lower.includes("firebase-installations")
        ) {
          keys.push(key);
        }
      }
      keys.forEach((key) => {
        try {
          storage.removeItem(key);
          changed = true;
        } catch (_) {}
      });
    };

    clearMatchingKeys(window.localStorage);
    clearMatchingKeys(window.sessionStorage);

    if (window.indexedDB?.databases) {
      const dbs = await window.indexedDB.databases();
      await Promise.all(
        dbs
          .filter((db) => String(db?.name || "").toLowerCase().includes("firebase"))
          .map((db) => new Promise((resolve) => {
            try {
              const req = window.indexedDB.deleteDatabase(db.name);
              req.onsuccess = () => { changed = true; resolve(true); };
              req.onerror = () => resolve(false);
              req.onblocked = () => resolve(false);
            } catch (_) {
              resolve(false);
            }
          }))
      );
    }

    // After repair, force a fresh persistence attempt on the next login.
    _persistencePromise = null;
    console.info("[auth] Firebase auth cache repaired");
    return changed;
  } catch (e) {
    console.warn("[auth] repair failed:", e?.message || e);
    _persistencePromise = null;
    return changed;
  }
}

export function initAppCheck() {
  try {
    return initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY
      ),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    console.warn("[AppCheck] init skipped:", e?.message || e);
    return null;
  }
}

export function logFirebaseConfig() {
  try {
    console.info("[firebase] projectId:", firebaseConfig.projectId);
  } catch {
    // noop
  }
}

if (typeof window !== 'undefined') {
  window.firebaseAuth = auth;
}

export const getAuthClient = () => auth;
export { app, firebaseConfig };
export default app;
